// StorePurchase.js — Purchase entry that's wired directly to the Store master
// (StoreMasterQuick). Selecting an item auto-fills its unit + last-known
// stock, and saving the purchase increments stock on the server side via the
// existing /home/addAddPurchase endpoint (which now resolves StoreItemId →
// updates CurrentStock + writes a StockTransaction row).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';

// Empty row template — `storeItem` holds the picked StoreMasterQuick row
// (for live stock display), `storeItemId` is what we POST to the server so
// stock can be deterministically adjusted.
const EMPTY_ROW = {
  storeItemId: null,
  storeItem: null,
  itemName: '',
  unit: '',
  qty: '',
  rate: '',
  gst: '',
  amount: 0,
  gstAmount: 0,
  // Expiry Management (batch-wise). Expiry required only when the picked
  // store item has trackExpiry on — enforced in getValidationError().
  batchNumber: '',
  mfgDate: '',
  expiryDate: '',
};

export default function StorePurchase() {
  const token = sessionStorage.getItem('token');

  // ─── Header / payment ──────────────────────────────────────────────────
  const [form, setForm] = useState({
    vendorName: '',
    billNo: '',
    billDate: '',
    remark: '',
    supplierId: null,
    supplier: null, // full supplier object — used for showing GST/contact below
  });
  const [payment, setPayment] = useState({
    mode: 'Cash',
    cardnumber: '',
    cardtype: '',
    refno: '',
  });

  // ─── Items ─────────────────────────────────────────────────────────────
  const [items, setItems] = useState([{ ...EMPTY_ROW }]);

  // ─── Store master data ─────────────────────────────────────────────────
  const [storeItems, setStoreItems] = useState([]);
  const [storeLoading, setStoreLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  // Pending indents (orders raised) for the SELECTED supplier — shown below the
  // supplier picker so the user sees what was ordered before entering the bill.
  const [supplierIndents, setSupplierIndents] = useState([]);
  // Bumped after a purchase / cancel to re-pull the indents panel.
  const [indentRefresh, setIndentRefresh] = useState(0);
  const [cancellingIndent, setCancellingIndent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // Fetch the store master once on mount. We re-fetch after a successful save
  // too so the in-page stock numbers stay fresh without a full page reload.
  const fetchStore = async () => {
    if (!company?.id || !token) {
      setStoreLoading(false);
      return;
    }
    setStoreLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetStoreItems/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setStoreItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load store items:', err);
      notify('Failed to load store items', 'error');
    } finally {
      setStoreLoading(false);
    }
  };

  // Suppliers fetched separately — failure here shouldn't block purchase
  // entry (user can still type a vendor name manually as fallback).
  const fetchSuppliers = async () => {
    if (!company?.id || !token) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetSuppliers/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const data = await res.json();
      // Only active suppliers in the dropdown; inactive show only in reports.
      setSuppliers(
        (Array.isArray(data) ? data : []).filter((s) => s.activeStatus !== false),
      );
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  useEffect(() => {
    fetchStore();
    fetchSuppliers();
  }, []);

  // When a supplier is picked, load THAT supplier's PENDING indents so the user
  // can see what was ordered before entering the bill. Cleared when the
  // supplier is unset or a vendor is typed manually (no supplierId).
  useEffect(() => {
    const sid = form.supplierId;
    if (!sid || !company?.id) {
      setSupplierIndents([]);
      return;
    }
    let alive = true;
    fetch(`${config.API_BASE_URL}/indent/list/${company.id}?status=Pending`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!alive) return;
        const rows = (Array.isArray(data) ? data : []).filter(
          (x) =>
            Number(x.supplierId) === Number(sid) &&
            Number(x.remaining ?? x.qty) > 0,
        );
        setSupplierIndents(rows);
      })
      .catch(() => {
        if (alive) setSupplierIndents([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supplierId, indentRefresh]);

  // ─── Row math ──────────────────────────────────────────────────────────
  const recalcRow = (row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    const gst = parseFloat(row.gst) || 0;
    const amount = qty * rate;
    const gstAmount = (amount * gst) / 100;
    return { ...row, amount, gstAmount };
  };

  const handleFieldChange = (i, field, value) => {
    const updated = [...items];
    updated[i] = recalcRow({ ...updated[i], [field]: value });
    setItems(updated);
  };

  const handlePickStoreItem = (i, option) => {
    const updated = [...items];
    if (option && typeof option === 'object') {
      updated[i] = recalcRow({
        ...updated[i],
        storeItem: option,
        storeItemId: option.id ?? option.Id ?? null,
        itemName: option.storeName || option.StoreName || '',
        unit: option.unit || option.Unit || '',
      });
    } else {
      // Free-text fallback — purchase still saves, just no stock impact unless
      // the name matches an existing store item server-side.
      updated[i] = recalcRow({
        ...updated[i],
        storeItem: null,
        storeItemId: null,
        itemName: typeof option === 'string' ? option : '',
        unit: '',
      });
    }
    setItems(updated);
  };

  const addRow = () => setItems([...items, { ...EMPTY_ROW }]);
  const removeRow = (i) => {
    if (items.length === 1) {
      setItems([{ ...EMPTY_ROW }]);
      return;
    }
    setItems(items.filter((_, idx) => idx !== i));
  };

  // ─── Totals ────────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => items.reduce((s, x) => s + (x.amount || 0), 0),
    [items],
  );
  const totalGst = useMemo(
    () => items.reduce((s, x) => s + (x.gstAmount || 0), 0),
    [items],
  );
  const grandTotal = subtotal + totalGst;

  // ─── Validation ────────────────────────────────────────────────────────
  const getValidationError = () => {
    if (!form.vendorName.trim()) return 'Vendor Name is required';
    if (!form.billNo.trim()) return 'Bill No is required';
    if (!form.billDate) return 'Bill Date is required';
    if (payment.mode === 'Card' && !payment.cardnumber.trim())
      return 'Card Number is required for Card payment';
    if (
      (payment.mode === 'Online' || payment.mode === 'UPI') &&
      !payment.refno.trim()
    )
      return 'Reference No is required for Online/UPI payment';

    const validItems = items.filter(
      (r) => r.itemName.trim() && parseFloat(r.qty) > 0 && parseFloat(r.rate) > 0,
    );
    if (validItems.length === 0)
      return 'Add at least one item with name, qty and rate';

    for (let idx = 0; idx < items.length; idx++) {
      const r = items[idx];
      const hasAny = r.itemName.trim() || r.qty || r.rate;
      if (!hasAny) continue;
      if (!r.itemName.trim()) return `Row ${idx + 1}: Item is required`;
      if (!(parseFloat(r.qty) > 0)) return `Row ${idx + 1}: Qty must be > 0`;
      if (!(parseFloat(r.rate) > 0)) return `Row ${idx + 1}: Rate must be > 0`;
      // Expiry Management: required for Track-Expiry items; never before bill date.
      if (r.storeItem?.trackExpiry && !r.expiryDate)
        return `Row ${idx + 1}: Expiry Date is required for "${r.itemName.trim()}" (Track Expiry is on)`;
      if (r.expiryDate && form.billDate && r.expiryDate < form.billDate)
        return `Row ${idx + 1}: Expiry Date cannot be earlier than Bill Date`;
    }
    return null;
  };

  // Cancel the remaining of a pending indent line (e.g. ordered 100, bought 70,
  // drop the leftover 30). No stock change — just marks the line Cancelled.
  const cancelIndent = async (detailId) => {
    setCancellingIndent(detailId);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/indent/cancel/${detailId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        notify(d?.error || 'Cancel failed', 'error');
        return;
      }
      notify('Indent cancelled');
      setIndentRefresh((x) => x + 1);
    } catch (e) {
      notify(e.message || 'Cancel failed', 'error');
    } finally {
      setCancellingIndent(null);
    }
  };

  // ─── Save ──────────────────────────────────────────────────────────────
  const savePurchase = async () => {
    const err = getValidationError();
    if (err) {
      notify(err, 'warning');
      return;
    }

    const cleanItems = items
      .filter((r) => r.itemName.trim() && parseFloat(r.qty) > 0)
      .map((r) => ({
        itemName: r.itemName.trim(),
        storeItemId: r.storeItemId || 0,
        qty: parseFloat(r.qty) || 0,
        rate: parseFloat(r.rate) || 0,
        gst: parseFloat(r.gst) || 0,
        // Line total INCLUDING gst so backend's TotalAmount reflects what
        // the user saw on screen.
        amount: (r.amount || 0) + (r.gstAmount || 0),
        // Batch-wise expiry (nullable). Written to StockBatch by the repo.
        batchNumber: r.batchNumber?.trim() || null,
        mfgDate: r.mfgDate || null,
        expiryDate: r.expiryDate || null,
      }));

    const payload = {
      vendorName: form.vendorName.trim(),
      supplierId: form.supplierId || null,
      billNo: form.billNo.trim(),
      billDate: form.billDate,
      remark: form.remark.trim(),
      cardnumber: payment.mode === 'Card' ? payment.cardnumber : null,
      cardtype: payment.mode === 'Card' ? payment.cardtype : null,
      Companyid: company?.id,
      refno:
        payment.mode === 'Online' || payment.mode === 'UPI'
          ? payment.refno
          : null,
      items: cleanItems,
    };

    try {
      setSaving(true);
      const res = await fetch(`${config.API_BASE_URL}/home/addAddPurchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const body = await res.json();
      if (body.status !== 'success')
        throw new Error(body.message || 'Save failed');

      notify('Purchase saved & stock updated');

      // Fulfil this supplier's matching pending indents — fully purchased lines
      // complete (hide), partial ones keep the remaining qty pending.
      // Non-blocking; purchase already succeeded.
      try {
        const indentItems = cleanItems
          .filter((i) => i.storeItemId > 0)
          .map((i) => ({ ProductId: i.storeItemId, Qty: i.qty }));
        if (form.supplierId && indentItems.length > 0) {
          await fetch(`${config.API_BASE_URL}/indent/applypurchase`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              CompanyId: company?.id,
              SupplierId: form.supplierId,
              Items: indentItems,
            }),
          });
          setIndentRefresh((x) => x + 1); // refresh the indents panel below
        }
      } catch {
        /* non-blocking */
      }

      // Vendor/supplier kept (likely entering multiple bills for same vendor),
      // the rest reset so the form is immediately reusable.
      setForm({ ...form, billNo: '', remark: '' });
      setItems([{ ...EMPTY_ROW }]);
      setPayment({ mode: 'Cash', cardnumber: '', cardtype: '', refno: '' });
      fetchStore(); // refresh stock numbers for the next entry
    } catch (e) {
      notify(e.message || 'Failed to save purchase', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
          justifyContent: 'center',
        }}>
        <StorefrontIcon sx={{ fontSize: 32, color: '#1565c0' }} />
        <Typography variant='h4' fontWeight='bold'>
          Store Purchase
        </Typography>
      </Box>

      {/* Header form */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 2 }}>
            Purchase Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {/* Pick from supplier master so reports/outstanding stay clean.
                  freeSolo lets the user type a vendor not yet in the master
                  (it'll save as VendorName-only, no SupplierId). */}
              <Autocomplete
                freeSolo
                options={suppliers}
                getOptionLabel={(opt) =>
                  typeof opt === 'string'
                    ? opt
                    : opt?.supplierName || ''
                }
                value={form.supplier || form.vendorName}
                onChange={(_, v) => {
                  if (v && typeof v === 'object') {
                    setForm({
                      ...form,
                      supplier: v,
                      supplierId: v.id,
                      vendorName: v.supplierName || '',
                    });
                  } else {
                    setForm({
                      ...form,
                      supplier: null,
                      supplierId: null,
                      vendorName: typeof v === 'string' ? v : '',
                    });
                  }
                }}
                onInputChange={(_, newInput, reason) => {
                  if (reason === 'input') {
                    setForm({
                      ...form,
                      vendorName: newInput,
                      // Typing breaks the supplier link — null it out so we
                      // don't accidentally tie a typed vendor to the wrong row.
                      supplier: null,
                      supplierId: null,
                    });
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {opt.supplierName}
                      </Typography>
                      <Typography fontSize={11} color='text.secondary'>
                        {opt.mobileNumber || '—'}
                        {opt.gstNumber ? ` · GST: ${opt.gstNumber}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label='Supplier *'
                    size='small'
                    placeholder='Pick or type vendor name'
                  />
                )}
              />
              {form.supplier && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mt: 0.5 }}>
                  {form.supplier.mobileNumber || ''}
                  {form.supplier.gstNumber
                    ? ` · GST: ${form.supplier.gstNumber}`
                    : ''}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label='Bill No'
                fullWidth
                size='small'
                value={form.billNo}
                onChange={(e) => setForm({ ...form, billNo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type='date'
                label='Bill Date'
                InputLabelProps={{ shrink: true }}
                fullWidth
                size='small'
                value={form.billDate}
                onChange={(e) =>
                  setForm({ ...form, billDate: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label='Payment Mode'
                fullWidth
                size='small'
                value={payment.mode}
                onChange={(e) =>
                  setPayment({
                    mode: e.target.value,
                    cardnumber: '',
                    cardtype: '',
                    refno: '',
                  })
                }>
                <MenuItem value='Cash'>Cash</MenuItem>
                <MenuItem value='Card'>Card</MenuItem>
                <MenuItem value='Online'>Online</MenuItem>
                <MenuItem value='UPI'>UPI</MenuItem>
                <MenuItem value='Credit'>Credit (Pay Later)</MenuItem>
              </TextField>
            </Grid>

            {payment.mode === 'Card' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label='Card Number'
                    fullWidth
                    size='small'
                    value={payment.cardnumber}
                    onChange={(e) =>
                      setPayment({ ...payment, cardnumber: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label='Card Type'
                    fullWidth
                    size='small'
                    value={payment.cardtype}
                    onChange={(e) =>
                      setPayment({ ...payment, cardtype: e.target.value })
                    }>
                    <MenuItem value='Debit'>Debit</MenuItem>
                    <MenuItem value='Credit'>Credit</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {(payment.mode === 'Online' || payment.mode === 'UPI') && (
              <Grid item xs={12} md={4}>
                <TextField
                  label='Reference No'
                  fullWidth
                  size='small'
                  value={payment.refno}
                  onChange={(e) =>
                    setPayment({ ...payment, refno: e.target.value })
                  }
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </motion.div>

      {/* Pending indents for the selected supplier — what we've already ordered
          from them. Shows ONLY when a master supplier is picked AND they have
          pending indents; otherwise nothing (purchase flow stays unchanged). */}
      {form.supplierId && supplierIndents.length > 0 && (
        <Paper
          sx={{
            mt: 3,
            borderRadius: 3,
            border: '1px solid #ffe0b2',
            bgcolor: '#fff8ef',
          }}>
          <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #ffe0b2' }}>
            <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
              Pending orders for {form.vendorName || 'this supplier'} (
              {supplierIndents.length})
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Items raised on an indent for this supplier. Enter the purchase
              below — a fully-purchased item clears automatically; a partial one
              keeps its remaining qty. Use Cancel to drop the remaining.
            </Typography>
          </Box>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ background: '#fff3e0' }}>
                  {[
                    'Order No',
                    'Date',
                    'Product',
                    'Ordered',
                    'Remaining',
                    'Price',
                    '',
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {supplierIndents.map((r) => {
                  const remaining = Number(r.remaining ?? r.qty);
                  return (
                    <TableRow key={r.detailId} hover>
                      <TableCell sx={{ fontWeight: 700, color: '#e65100' }}>
                        {r.orderNo}
                      </TableCell>
                      <TableCell>
                        {r.orderDate
                          ? new Date(r.orderDate).toLocaleDateString('en-GB')
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.productName}
                      </TableCell>
                      <TableCell>{Number(r.qty)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#e65100' }}>
                        {remaining}
                      </TableCell>
                      <TableCell>{Number(r.price || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          color='error'
                          variant='outlined'
                          disabled={cancellingIndent === r.detailId}
                          onClick={() => cancelIndent(r.detailId)}
                          sx={{ textTransform: 'none', fontSize: 11 }}>
                          Cancel
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Items table */}
      <Paper sx={{ mt: 3, borderRadius: 3 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
          }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
            Items
          </Typography>
          {storeLoading && (
            <Chip
              size='small'
              icon={<CircularProgress size={14} />}
              label='Loading store items...'
            />
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#0a2540' }}>
                {[
                  'Store Item',
                  'Unit',
                  'Current Stock',
                  'Qty',
                  'Rate',
                  'GST %',
                  'Amount',
                  'GST Amt',
                  'Total',
                  '',
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row, i) => {
                const liveStock = row.storeItem
                  ? Number(
                      row.storeItem.currentStock ??
                        row.storeItem.CurrentStock ??
                        0,
                    )
                  : null;
                return (
                  <React.Fragment key={i}>
                  <TableRow>
                    <TableCell sx={{ minWidth: 240 }}>
                      <Autocomplete
                        freeSolo
                        options={storeItems}
                        getOptionLabel={(opt) =>
                          typeof opt === 'string'
                            ? opt
                            : opt?.storeName || opt?.StoreName || ''
                        }
                        value={row.storeItem || row.itemName}
                        onChange={(_, newVal) => handlePickStoreItem(i, newVal)}
                        onInputChange={(_, newInput, reason) => {
                          if (reason === 'input')
                            handleFieldChange(i, 'itemName', newInput);
                        }}
                        renderOption={(props, opt) => (
                          <li {...props} key={opt.id ?? opt.Id}>
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {opt.storeName || opt.StoreName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: 'text.secondary',
                                }}>
                                Stock:{' '}
                                {Number(
                                  opt.currentStock ?? opt.CurrentStock ?? 0,
                                )}{' '}
                                {opt.unit || opt.Unit}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size='small'
                            placeholder='Search store item'
                            fullWidth
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 70 }}>
                      <Typography sx={{ fontSize: 13 }}>
                        {row.unit || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      {liveStock != null ? (
                        <Chip
                          size='small'
                          label={`${liveStock} ${row.unit || ''}`}
                          color={liveStock <= 0 ? 'error' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: 12, color: 'text.secondary' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        inputProps={{ min: 0, step: 'any' }}
                        value={row.qty}
                        onChange={(e) =>
                          handleFieldChange(i, 'qty', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        inputProps={{ min: 0, step: 'any' }}
                        value={row.rate}
                        onChange={(e) =>
                          handleFieldChange(i, 'rate', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 110 }}>
                      <TextField
                        fullWidth
                        size='small'
                        type='number'
                        inputProps={{ min: 0, max: 100, step: 'any' }}
                        value={row.gst}
                        onChange={(e) =>
                          handleFieldChange(i, 'gst', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {Companycurrency}
                      {row.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {Companycurrency}
                      {row.gstAmount.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {Companycurrency}
                      {(row.amount + row.gstAmount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => removeRow(i)}>
                        <DeleteIcon color='error' fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {row.storeItemId && (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ py: 1, bgcolor: row.storeItem?.trackExpiry ? '#FFF7ED' : '#F8FAFC' }}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: row.storeItem?.trackExpiry ? '#C2410C' : 'text.secondary' }}>
                            {row.storeItem?.trackExpiry ? 'Batch / Expiry (required)' : 'Batch / Expiry (optional)'}
                          </Typography>
                          <TextField
                            label='Batch No'
                            size='small'
                            value={row.batchNumber}
                            onChange={(e) => handleFieldChange(i, 'batchNumber', e.target.value)}
                            sx={{ width: 150 }}
                          />
                          <TextField
                            label='Mfg Date'
                            type='date'
                            size='small'
                            InputLabelProps={{ shrink: true }}
                            value={row.mfgDate}
                            onChange={(e) => handleFieldChange(i, 'mfgDate', e.target.value)}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label='Expiry Date'
                            type='date'
                            size='small'
                            required={!!row.storeItem?.trackExpiry}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: form.billDate || undefined }}
                            value={row.expiryDate}
                            onChange={(e) => handleFieldChange(i, 'expiryDate', e.target.value)}
                            error={!!row.storeItem?.trackExpiry && !row.expiryDate}
                            sx={{ width: 170 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <Button startIcon={<AddIcon />} onClick={addRow} variant='outlined'>
            Add Item
          </Button>
        </Box>
      </Paper>

      {/* Summary */}
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography color='text.secondary' fontSize={13}>
              Subtotal
            </Typography>
            <Typography variant='h6'>
              {Companycurrency}
              {subtotal.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color='text.secondary' fontSize={13}>
              Total GST
            </Typography>
            <Typography variant='h6'>
              {Companycurrency}
              {totalGst.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color='text.secondary' fontSize={13}>
              Grand Total
            </Typography>
            <Typography variant='h5' sx={{ fontWeight: 800, color: '#1565c0' }}>
              {Companycurrency}
              {grandTotal.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <TextField
          label='Remark'
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 1 }}
          value={form.remark}
          onChange={(e) => setForm({ ...form, remark: e.target.value })}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant='contained'
            size='large'
            onClick={savePurchase}
            disabled={saving}>
            {saving ? 'Saving...' : 'Save Purchase & Update Stock'}
          </Button>
          <Typography variant='caption' color='text.secondary'>
            Stock will be auto-incremented for picked store items.
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
